import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL || '/api';

export interface ScheduleTask {
  id: number;
  taskNumber?: string | null;
  name?: string | null;
  description?: string | null;
  identifiedDate?: string | null;
  startDate: string | null;
  plannedCompletionDate: string | null;
  /** Planned quantity / accomplishment (same as Task.identifiedAccomplishment). */
  identifiedAccomplishment?: number | null;
  status: string;
  foremanAreaId?: number | null;
  projectId?: number | null;
  /** Present on schedule task list payloads */
  foremanArea?: { id: number; name: string; code?: string | null } | null;
  serviceArea?: { id: number; name: string; code?: string | null } | null;
  /**
   * When true, the task is shown across the visible schedule range (year-round / as-needed work)
   * even without planned start or end dates.
   */
  yearRoundSchedule?: boolean;
  dependencies?: {
    triggers: string[];
    otherComment?: string | null;
    resourceDependentType?: string | null;
    completionTaskId?: number | null;
  } | null;
  vehicles?: Array<{
    unitUsed: string | null;
    unitOfMeasure: string | null;
  }>;
  activities?: Array<{
    id: number;
    activityNumber: string;
    activityName: string;
  }>;
}

export interface ScheduleTaskAssignee {
  id: number;
  name: string;
  lastName: string;
  roleName: string | null;
}

export interface ScheduleTaskProject {
  id: number;
  name: string;
  projectNumber: string | null;
}

export interface ScheduleTaskForChart extends ScheduleTask {
  taskNumber?: string | null;
  type?: string;
  project: ScheduleTaskProject | null;
  assignees: ScheduleTaskAssignee[];
}

export interface ScheduleResource {
  id: number;
  name: string;
  lastName: string;
  roleName: string | null;
  foremanArea: { id: number; name: string } | null;
  serviceArea: { id: number; name: string } | null;
  position: string | null;
  tasks: ScheduleTask[];
}

export interface ScheduleVehicle {
  unitNumber: string;
  /**
   * Equipment type for Schedule UI: VehicleManagement.vehicleClassification, then vehicleType if classification is empty.
   */
  vehicleType: string | null;
  serviceAreas: Array<{ id: number; name: string }>;
}

export interface GetScheduleResourcesParams {
  dateFrom: string;
  dateTo: string;
  /**
   * Service area id(s) or name(s). Multiselect callers pass a comma-separated list
   * (`toScheduleListParam`); a single bare value is still accepted. Omit for "all".
   */
  serviceArea?: string;
  /** Foreman area id(s) or name(s) — comma-separated list or a single bare value. */
  foremanArea?: string;
  employeeName?: string;
  role?: string;
  /** Work type(s) — comma-separated list or a single bare value; "All" means no filter. */
  workType?: string;
  includeUnassignedVehicleTasks?: boolean;
  /** When true, server requires schedule:people in addition to schedule access. */
  peopleSchedule?: boolean;
  taskSearch?: string;
  statuses?: string[];
}

export async function getScheduleResources(
  params: GetScheduleResourcesParams
): Promise<ScheduleResource[]> {
  const query = new URLSearchParams();
  query.set('dateFrom', params.dateFrom);
  query.set('dateTo', params.dateTo);
  if (params.serviceArea) query.set('serviceArea', params.serviceArea);
  if (params.foremanArea) query.set('foremanArea', params.foremanArea);
  if (params.employeeName) query.set('employeeName', params.employeeName);
  if (params.role) query.set('role', params.role);
  if (params.workType) query.set('workType', params.workType);
  if (params.includeUnassignedVehicleTasks) query.set('includeUnassignedVehicleTasks', 'true');
  if (params.peopleSchedule) query.set('peopleSchedule', 'true');
  const response = await axios.get<ScheduleResource[]>(
    `${BASE_URL}/schedule/resources?${query.toString()}`
  );
  return response.data;
}

export async function getScheduleTasks(
  params: GetScheduleResourcesParams
): Promise<ScheduleTaskForChart[]> {
  const query = new URLSearchParams();
  query.set('dateFrom', params.dateFrom);
  query.set('dateTo', params.dateTo);
  if (params.serviceArea) query.set('serviceArea', params.serviceArea);
  if (params.foremanArea) query.set('foremanArea', params.foremanArea);
  if (params.workType) query.set('workType', params.workType);
  if (params.taskSearch) query.set('taskSearch', params.taskSearch);
  const statusFilter = (params.statuses || []).map((s) => String(s || '').trim()).filter((s) => s.length > 0);
  if (statusFilter.length > 0) query.set('statuses', statusFilter.join(','));
  const response = await axios.get<ScheduleTaskForChart[]>(
    `${BASE_URL}/schedule/tasks?${query.toString()}`
  );
  return response.data;
}

/** `serviceArea` / `foremanArea` accept a comma-separated list of ids or names, or a single value. */
export async function getScheduleVehicles(params: { serviceArea?: string; foremanArea?: string }): Promise<ScheduleVehicle[]> {
  const query = new URLSearchParams();
  if (params.serviceArea) query.set('serviceArea', params.serviceArea);
  if (params.foremanArea) query.set('foremanArea', params.foremanArea);

  const response = await axios.get<ScheduleVehicle[]>(`${BASE_URL}/schedule/vehicles?${query.toString()}`);
  return response.data;
}
